<template>
  <div class="table-responsive">
    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <table class="table table-hover" v-if="!loading">
      <thead>
        <tr class="border-bottom-primary">
          <th scope="col" v-for="(col, idx) in columns" :key="idx">
            <slot :name="`header-${col.value}`" :column="col">{{ col.text || col.value }}</slot>
          </th>
          <th v-if="showActions">Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, rowIndex) in rows" :key="rowIndex" :class="rowClass ? rowClass(item) : null">
          <td v-for="(col, colIndex) in columns" :key="colIndex" :class="rowClass ? rowClass(item) : null">
            <slot :name="`cell-${col.value}`" :item="item" :value="item[col.value]">
              <template v-if="isImageColumn(col.value)">
                <img
                  v-if="getImageSrc(item[col.value])"
                  :src="getImageSrc(item[col.value])"
                  :alt="item[col.value]"
                  class="column-icon"
                />
                <template v-else>{{ item[col.value] }}</template>
              </template>
              <template v-else-if="isBooleanLike(item[col.value])">
                <input type="checkbox" disabled :checked="toBoolean(item[col.value])" />
              </template>
              <template v-else>
                {{ formatCell(item[col.value], col.value) }}
              </template>
            </slot>
          </td>
          <td v-if="showActions" :class="rowClass ? rowClass(item) : null">
            <slot name="actions" :item="item">
              <button class="btn btn-xs btn-info icon-btn" @click="$emit('view', item)" title="Detalles">
                <vue-feather type="eye" size="16"></vue-feather>
              </button>
              <span class="mx-1"></span>
              <button class="btn btn-xs btn-warning icon-btn" @click="$emit('edit', item)" title="Editar">
                <vue-feather type="edit" size="16"></vue-feather>
              </button>
              <!--<span class="mx-1"></span>
              <button class="btn btn-xs btn-danger icon-btn" @click="$emit('delete', item)" aria-label="Eliminar" title="Eliminar">
                <vue-feather type="trash-2" size="16"></vue-feather>
              </button> -->
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { getAseguradoraLogo } from '@/utils/aseguradoraLogo';

export default {
  name: 'DataTable',
  props: {
    // rows: array of objects to render (should be already paginated by the parent)
    rows: { type: Array, default: () => [] },
    // columns: array of { text, value } describing which fields to render (order matters)
    columns: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: [String, null], default: null },
    // showActions: whether to show the actions column
    showActions: { type: Boolean, default: true },
    // imageColumns: array of column names that should display images
    imageColumns: { type: Array, default: () => [] },
    // imagePath: base path for images (used with imageColumns)
    imagePath: { type: String, default: '' },
    // imageExtension: extension for images
    imageExtension: { type: String, default: 'svg' },
    // rowClass: optional function(item) => class value applied to each <tr>
    rowClass: { type: Function, default: null },
  },
  methods: {
    isImageColumn(colName) {
      return this.imageColumns.includes(colName);
    },
    getImageSrc(value) {
      return getAseguradoraLogo(value) || '';
    },
    isBooleanLike(val) {
      if (typeof val === 'boolean') return true;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return v === 'true' || v === 'false';
      }
      return false;
    },
    toBoolean(val) {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'string') return val.trim().toLowerCase() === 'true';
      return Boolean(val);
    },
    formatDateTime(val) {
      if (val == null) return '';
      let d;
      if (val instanceof Date) {
        d = val;
      } else if (this.isIsoDateString(String(val))) {
        d = new Date(val);
      } else {
        const parsed = Date.parse(val);
        if (!isNaN(parsed)) d = new Date(parsed);
        else return val;
      }

      if (isNaN(d)) return val;

      const pad = n => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = String(d.getFullYear());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      const seconds = pad(d.getSeconds());
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },
    isIsoDateString(val) {
      if (typeof val !== 'string') return false;
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
      const isoRegexNoT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      return isoRegex.test(val) || isoRegexNoT.test(val);
    },
    formatCell(val, key) {
      if (val == null) return '';
      if (key && /(^id$|id$)/i.test(key)) {
        return String(val);
      }
      if (typeof val === 'object') return JSON.stringify(val);
      // Solo formatear como fecha si es una cadena ISO válida
      if (this.isIsoDateString(val)) {
        return this.formatDateTime(val);
      }
      // Devolver cadenas y números sin transformar
      return String(val);
    },
  },
};
</script>

<style scoped>
/* Center feather SVG icons inside small action buttons */
.icon-btn { 
  width: 30px; 
  height: 30px; 
  padding: 0; 
  display: inline-flex; 
  align-items: center; 
  justify-content: center; 
}
.column-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
</style>
